import { BadgeDollarSign, FileCheck2, MapPinned } from "lucide-react";
import { useId, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { fundingLabels, valueLabels } from "../lib/format";
import { projectValue } from "../lib/transparency";
import type { Project } from "../types/content";
import { Card, CardContent, CardHeader } from "./ui/Card";

function ScopeFact({ value }: { value: string }) {
  const [summary, details = ""] = value.includes(":") ? value.split(/:(.*)/s, 2) : ["", value];
  const items = details
    .split(/\s*·\s*|,\s*|\s+e\s+(?=\d)/i)
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length < 2) return <>{value}</>;

  return (
    <>
      {summary && <span className="project-fact-card__summary">{summary}</span>}
      <ul className="project-fact-card__list">
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </>
  );
}

export function ProjectFacts({ project }: { project: Project }) {
  // Adapted from Aceternity UI's official card-hover-effect registry component.
  // Informational cards remain noninteractive; layout IDs are isolated per project.
  const [hovered, setHovered] = useState<string | null>(null);
  const highlightId = useId();
  const reduced = useReducedMotion();
  const facts = [
    { icon: BadgeDollarSign, kind: "value", label: project.valueType && project.valueType !== 'em_validacao' ? valueLabels[project.valueType] : 'Valor', value: projectValue(project) },
    { icon: MapPinned, kind: "scope", label: "Escopo", value: project.scope ?? "A confirmar" },
    { icon: FileCheck2, kind: "origin", label: "Origem", value: fundingLabels[project.fundingType] },
  ];

  return (
    <dl className="project-facts">
      {facts.map(({ icon: Icon, kind, label, value }) => (
        <Card key={label} className={`project-fact-card project-fact-card--${kind}`} onMouseEnter={() => setHovered(label)} onMouseLeave={() => setHovered(null)}>
          <AnimatePresence>
            {hovered === label && <motion.span aria-hidden="true" className="project-fact-highlight" layoutId={reduced ? undefined : highlightId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.15 }} />}
          </AnimatePresence>
          <CardHeader>
            <span className="project-fact-card__icon"><Icon aria-hidden="true" /></span>
            <dt>{label}</dt>
          </CardHeader>
          <CardContent><dd>{kind === "scope" ? <ScopeFact value={value} /> : value}</dd></CardContent>
        </Card>
      ))}
    </dl>
  );
}
